// Static metadata for the IfcXxx.PredefinedType attribute on the
// classes the IFCEdit UI exposes.
//
// PredefinedType is an enum that varies per class AND per schema:
//
//   * The STEP-arg INDEX where it lives depends on the inherited
//     attribute count of the class. For typical IfcElement subclasses
//     (Wall, Slab, Beam, ...) it sits at 8 (after Tag at 7). IfcSpace
//     anchors it at 9 (after LongName=7, CompositionType=8). IfcDoor /
//     IfcWindow push it further out because of OverallHeight/Width.
//
//   * The set of LEGAL VALUES is named IfcXxxTypeEnum (e.g.
//     IfcWallTypeEnum). The values themselves change between
//     schemas: IFC2X3 IfcWallTypeEnum has different members than
//     IFC4 IfcWallTypeEnum. The generated ifcEnums table covers all
//     three real schemas; IFC4X1/IFC4X2 alias to IFC4.
//
// Many classes only acquired PredefinedType in IFC4 (IfcWall,
// IfcBeam, IfcColumn, IfcDoor, IfcWindow, IfcSpace, ...): in IFC2X3
// you set the type via the associated IfcXxxType through
// IfcRelDefinesByType. We track that with `schemas: ALL_IFC4PLUS`
// so the UI doesn't try to edit a non-existent slot.

import { IFC_ENUM_VALUES, type IfcEnumSchema } from './ifcEnums.generated'

const ALL_IFC4PLUS = [
  'IFC4',
  'IFC4X1',
  'IFC4X2',
  'IFC4X3',
] as const satisfies readonly IfcEnumSchema[]

const ALL_SCHEMAS = [
  'IFC2X3',
  ...ALL_IFC4PLUS,
] as const satisfies readonly IfcEnumSchema[]

interface PredefinedTypeMeta {
  /** STEP-arg index where PredefinedType lives. */
  index: number
  /** Enum class name (matches an `IfcXxxTypeEnum` in the generated table). */
  enumClass: string
  /** Schemas where this attribute is present on this class. */
  schemas: readonly IfcEnumSchema[]
}

// Class metadata table. Names are canonical camel-case (the same form
// the inheritance map uses). Lookup is case-insensitive via the
// `canonicalise` helper below: Fragments and STEP lines surface the
// IFC class name in either case.
const META: Record<string, PredefinedTypeMeta> = {
  IfcWall: { index: 8, enumClass: 'IfcWallTypeEnum', schemas: ALL_IFC4PLUS },
  IfcWallStandardCase: {
    index: 8,
    enumClass: 'IfcWallTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcWallElementedCase: {
    index: 8,
    enumClass: 'IfcWallTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcCurtainWall: {
    index: 8,
    enumClass: 'IfcCurtainWallTypeEnum',
    schemas: ALL_IFC4PLUS,
  },

  IfcSlab: { index: 8, enumClass: 'IfcSlabTypeEnum', schemas: ALL_SCHEMAS },
  IfcSlabStandardCase: {
    index: 8,
    enumClass: 'IfcSlabTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcSlabElementedCase: {
    index: 8,
    enumClass: 'IfcSlabTypeEnum',
    schemas: ALL_IFC4PLUS,
  },

  IfcBeam: { index: 8, enumClass: 'IfcBeamTypeEnum', schemas: ALL_IFC4PLUS },
  IfcBeamStandardCase: {
    index: 8,
    enumClass: 'IfcBeamTypeEnum',
    schemas: ALL_IFC4PLUS,
  },

  IfcColumn: {
    index: 8,
    enumClass: 'IfcColumnTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcColumnStandardCase: {
    index: 8,
    enumClass: 'IfcColumnTypeEnum',
    schemas: ALL_IFC4PLUS,
  },

  IfcRoof: { index: 8, enumClass: 'IfcRoofTypeEnum', schemas: ALL_SCHEMAS },
  IfcCovering: {
    index: 8,
    enumClass: 'IfcCoveringTypeEnum',
    schemas: ALL_SCHEMAS,
  },

  IfcStair: { index: 8, enumClass: 'IfcStairTypeEnum', schemas: ALL_SCHEMAS },
  IfcStairFlight: {
    index: 8,
    enumClass: 'IfcStairFlightTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcRamp: { index: 8, enumClass: 'IfcRampTypeEnum', schemas: ALL_SCHEMAS },
  IfcRampFlight: {
    index: 8,
    enumClass: 'IfcRampFlightTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcRailing: {
    index: 8,
    enumClass: 'IfcRailingTypeEnum',
    schemas: ALL_SCHEMAS,
  },

  IfcMember: {
    index: 8,
    enumClass: 'IfcMemberTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcMemberStandardCase: {
    index: 8,
    enumClass: 'IfcMemberTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcPlate: { index: 8, enumClass: 'IfcPlateTypeEnum', schemas: ALL_IFC4PLUS },
  IfcPlateStandardCase: {
    index: 8,
    enumClass: 'IfcPlateTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcFooting: {
    index: 8,
    enumClass: 'IfcFootingTypeEnum',
    schemas: ALL_SCHEMAS,
  },
  IfcPile: { index: 8, enumClass: 'IfcPileTypeEnum', schemas: ALL_SCHEMAS },
  IfcChimney: {
    index: 8,
    enumClass: 'IfcChimneyTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcShadingDevice: {
    index: 8,
    enumClass: 'IfcShadingDeviceTypeEnum',
    schemas: ALL_IFC4PLUS,
  },

  IfcBuildingElementProxy: {
    index: 8,
    enumClass: 'IfcBuildingElementProxyTypeEnum',
    schemas: ALL_SCHEMAS,
  },
  IfcOpeningElement: {
    index: 8,
    enumClass: 'IfcOpeningElementTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcFurniture: {
    index: 8,
    enumClass: 'IfcFurnitureTypeEnum',
    schemas: ALL_IFC4PLUS,
  },

  IfcDuctSegment: {
    index: 8,
    enumClass: 'IfcDuctSegmentTypeEnum',
    schemas: ALL_SCHEMAS,
  },
  IfcPipeSegment: {
    index: 8,
    enumClass: 'IfcPipeSegmentTypeEnum',
    schemas: ALL_SCHEMAS,
  },
  IfcCableSegment: {
    index: 8,
    enumClass: 'IfcCableSegmentTypeEnum',
    schemas: ALL_SCHEMAS,
  },
  IfcAirTerminal: {
    index: 8,
    enumClass: 'IfcAirTerminalTypeEnum',
    schemas: ALL_SCHEMAS,
  },
  IfcSanitaryTerminal: {
    index: 8,
    enumClass: 'IfcSanitaryTerminalTypeEnum',
    schemas: ALL_SCHEMAS,
  },

  // Spatial: IfcSpace puts PredefinedType after CompositionType (8).
  IfcSpace: {
    index: 9,
    enumClass: 'IfcSpaceTypeEnum',
    schemas: ALL_IFC4PLUS,
  },

  // Door / Window: extra OverallHeight (8) + OverallWidth (9) before
  // PredefinedType (10).
  IfcDoor: {
    index: 10,
    enumClass: 'IfcDoorTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcDoorStandardCase: {
    index: 10,
    enumClass: 'IfcDoorTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcWindow: {
    index: 10,
    enumClass: 'IfcWindowTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  IfcWindowStandardCase: {
    index: 10,
    enumClass: 'IfcWindowTypeEnum',
    schemas: ALL_IFC4PLUS,
  },

  // ------------------------------------------------------------------
  // Group-like / non-geometric entities. Fragments returns near-empty
  // payloads for these; the PropertiesPanel augments `data` from
  // rawIfc so the synthesised Atributos rows still appear.
  // ------------------------------------------------------------------

  // IfcSystem layout: GlobalId, OwnerHistory, Name, Description,
  //   ObjectType, LongName (5), PredefinedType (6).
  // Enum is IfcDistributionSystemEnum (note: drops the "Type" suffix
  // compared to most other PredefinedType enums).
  IfcDistributionSystem: {
    index: 6,
    enumClass: 'IfcDistributionSystemEnum',
    schemas: ALL_IFC4PLUS,
  },
  // IfcBuildingSystem layout: GlobalId, OwnerHistory, Name, Description,
  //   ObjectType, PredefinedType (5), LongName (6). Different attribute
  //   ORDER than IfcDistributionSystem.
  IfcBuildingSystem: {
    index: 5,
    enumClass: 'IfcBuildingSystemTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  // IfcStructuralAnalysisModel layout: 0..4 IfcObject + 5 PredefinedType +
  //   rest. Enum is IfcAnalysisModelTypeEnum.
  IfcStructuralAnalysisModel: {
    index: 5,
    enumClass: 'IfcAnalysisModelTypeEnum',
    schemas: ALL_SCHEMAS,
  },
  // IfcSpatialZone: IfcSpatialElement + 8 PredefinedType.
  IfcSpatialZone: {
    index: 8,
    enumClass: 'IfcSpatialZoneTypeEnum',
    schemas: ALL_IFC4PLUS,
  },
  // IfcElementAssembly: IfcElement (0..7) + 8 AssemblyPlace +
  //   9 PredefinedType. Enum is IfcElementAssemblyTypeEnum.
  IfcElementAssembly: {
    index: 9,
    enumClass: 'IfcElementAssemblyTypeEnum',
    schemas: ALL_SCHEMAS,
  },
}

// Case-insensitive lookup index built once.
const META_BY_LOWER: Record<string, string> = Object.keys(META).reduce(
  (acc, k) => {
    acc[k.toLowerCase()] = k
    return acc
  },
  {} as Record<string, string>,
)

/** Canonicalise an IFC class name (UPPERCASE or mixed) to the camel-
 *  case form used by the META table. Returns null when unknown. */
function canonicalise(className: string): string | null {
  if (!className) return null
  return META_BY_LOWER[className.toLowerCase()] ?? null
}

export interface PredefinedTypeLookup {
  /** STEP-arg index of PredefinedType for this class. */
  index: number
  /** Sorted, deduplicated list of valid uppercase enum values. */
  choices: readonly string[]
}

/** Returns lookup info for PredefinedType editing on `className` /
 *  `schema`, or null when the attribute is not present (e.g. IFC2X3
 *  IfcWall) or the class isn't in the table. */
export function getPredefinedTypeInfo(
  className: string,
  schema: IfcEnumSchema | null,
): PredefinedTypeLookup | null {
  if (!schema) return null
  const canonical = canonicalise(className)
  if (!canonical) return null
  const meta = META[canonical]
  if (!meta.schemas.includes(schema)) return null
  const enums = IFC_ENUM_VALUES[schema]
  const values = enums?.[meta.enumClass]
  if (!values || values.length === 0) return null
  return { index: meta.index, choices: values }
}

/** True iff PredefinedType is editable on entities of this class on
 *  the given schema. Cheap predicate for the PropertiesPanel. */
export function hasPredefinedType(
  className: string,
  schema: IfcEnumSchema | null,
): boolean {
  return getPredefinedTypeInfo(className, schema) !== null
}
